import React from 'react';
import PropTypes from 'prop-types';
import { DateOnly } from '../utils/DateOnly';
import CalendarDay from './CalendarDay';
import '../styles/CalendarGrid.css';

/**
 * CalendarGrid component renders the calendar grid with day-of-week headers
 * and CalendarDay components for each day
 *
 * @param {Array} days - Array of day objects with date, day, year, month, isCurrentMonth
 * @param {Object} workoutsByDate - Object mapping date strings (YYYY-MM-DD) to workout arrays
 * @param {Object} triClubSchedule - Tri club schedule with weekly events
 * @param {boolean} showTimeSlots - Whether to show time slots (used for drag-drop state)
 * @param {Object} dragState - Drag state object { draggedWorkout, dragOverDate, dragOverTimeSlot }
 * @param {Function} onDragOver - Drag over handler
 * @param {Function} onDragLeave - Drag leave handler
 * @param {Function} onDrop - Drop handler
 * @param {Function} onWorkoutClick - Workout click handler
 * @param {Function} onWorkoutSelectionToggle - Workout selection toggle handler
 * @param {Function} onWorkoutDragStart - Workout drag start handler
 * @param {Function} onWorkoutDragEnd - Workout drag end handler
 */
function CalendarGrid({
  days,
  workoutsByDate,
  triClubSchedule,
  showTimeSlots,
  dragState,
  onDragOver,
  onDragLeave,
  onDrop,
  onWorkoutClick,
  onWorkoutSelectionToggle,
  onWorkoutHeatTrainingChange,
  onWorkoutDragStart,
  onWorkoutDragEnd,
  onAddWorkout,
  locations
}) {
  /**
   * Get the travel location city name for a given date string, or null if Chicago/default.
   * @param {string} dateStr - Date in YYYY-MM-DD format
   * @returns {string|null} City name if a non-default location entry covers this date
   */
  const getLocationForDate = (dateStr) => {
    if (!locations || !locations.length) return null;
    const loc = locations.find(l => l.startDate <= dateStr && l.endDate >= dateStr);
    return loc ? loc.cityName : null;
  };

  /**
   * Get workouts for a specific day
   * @param {number} day - Day of month
   * @param {number} dayYear - Year
   * @param {number} dayMonth - Month (0-indexed)
   * @returns {Array} Array of workouts for the day, sorted with selected first
   */
  const getWorkoutsForDay = (day, dayYear, dayMonth) => {
    if (!day) return [];
    // Create the date string in YYYY-MM-DD format without timezone conversion
    const dateStr = `${dayYear}-${String(dayMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayWorkouts = workoutsByDate[dateStr] || [];
    
    // Sort workouts: selected first, then unselected at bottom
    return dayWorkouts.sort((a, b) => {
      // If both have same selection status, maintain original order
      if (a.isSelected === b.isSelected) return 0;
      // Selected workouts come first (isSelected = true should come before false)
      return a.isSelected ? -1 : 1;
    });
  };

  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return !!window.matchMedia('(max-width: 420px)').matches;
  });
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia('(max-width: 420px)');
    const onChange = (e) => setIsMobile(!!e.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  const mobileDayMin = isMobile ? '140px' : null;
  const wrapperStyle = mobileDayMin ? { '--calendar-day-min': mobileDayMin } : undefined;

  return (
    <div
      className={`calendar-grid-wrapper week ${isMobile ? 'mobile' : ''}`}
      style={wrapperStyle}
    >
      <div className="day-of-week-headers week" data-mobile={isMobile ? 'true' : 'false'}>
        {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d) => (
          <div
            key={d}
            className="day-of-week"
          >
            {d}
          </div>
        ))}
      </div>

      <div
        className={`calendar-grid week ${isMobile ? 'mobile' : ''} ${showTimeSlots ? 'dragging' : ''}`}
        data-calendar-day-min={mobileDayMin || undefined}
      >
        {days.map((dayObj, index) => {
          const dayWorkouts = getWorkoutsForDay(dayObj.day, dayObj.year, dayObj.month);
          const isToday = dayObj.date.toDateString() === new Date().toDateString();
          const dateStr = `${dayObj.year}-${String(dayObj.month + 1).padStart(2, '0')}-${String(dayObj.day).padStart(2, '0')}`;
          const locationName = getLocationForDate(dateStr);

          return (
            <CalendarDay
              key={index}
              dayObj={dayObj}
              workouts={dayWorkouts}
              triClubSchedule={triClubSchedule}
              isToday={isToday}
              showTimeSlots={showTimeSlots}
              dragState={dragState}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onWorkoutClick={onWorkoutClick}
              onWorkoutSelectionToggle={onWorkoutSelectionToggle}
              onWorkoutHeatTrainingChange={onWorkoutHeatTrainingChange}
              onWorkoutDragStart={onWorkoutDragStart}
              onWorkoutDragEnd={onWorkoutDragEnd}
              onAddWorkout={onAddWorkout}
              locationName={locationName}
            />
          );
        })}
      </div>
    </div>
  );
}

CalendarGrid.propTypes = {
  days: PropTypes.arrayOf(
    PropTypes.shape({
      day: PropTypes.number.isRequired,
      year: PropTypes.number.isRequired,
      month: PropTypes.number.isRequired,
      date: PropTypes.instanceOf(Date).isRequired,
      isCurrentMonth: PropTypes.bool.isRequired,
    })
  ).isRequired,
  workoutsByDate: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string.isRequired,
        workoutType: PropTypes.string.isRequired,
        workoutDate: PropTypes.instanceOf(DateOnly).isRequired,
        originallyPlannedDay: PropTypes.string.isRequired,
        workoutDescription: PropTypes.string,
        plannedDuration: PropTypes.number,
        plannedDistanceInMeters: PropTypes.number,
        coachComments: PropTypes.string,
        isSelected: PropTypes.bool,
        timeOfDay: PropTypes.string,
        workoutLocation: PropTypes.string,
      })
    )
  ).isRequired,
  triClubSchedule: PropTypes.shape({
    effective_date: PropTypes.string,
    schedule: PropTypes.objectOf(
      PropTypes.arrayOf(
        PropTypes.shape({
          time: PropTypes.string.isRequired,
          activity: PropTypes.string.isRequired,
        })
      )
    ),
  }),
  showTimeSlots: PropTypes.bool.isRequired,
  dragState: PropTypes.shape({
    draggedWorkout: PropTypes.object,
    dragOverDate: PropTypes.string,
    dragOverTimeSlot: PropTypes.string,
  }).isRequired,
  onDragOver: PropTypes.func.isRequired,
  onDragLeave: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onWorkoutClick: PropTypes.func.isRequired,
  onWorkoutSelectionToggle: PropTypes.func.isRequired,
  onWorkoutHeatTrainingChange: PropTypes.func,
  onWorkoutDragStart: PropTypes.func.isRequired,
  onWorkoutDragEnd: PropTypes.func.isRequired,
  onAddWorkout: PropTypes.func,
  locations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    cityName: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  })),
};

CalendarGrid.defaultProps = {
  onWorkoutHeatTrainingChange: null,
  onAddWorkout: null,
  locations: [],
};

export default CalendarGrid;
